import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { SearchIcon, FilterIcon, ArrowUpDownIcon, LayersIcon, ClockIcon, AlertTriangleIcon, XIcon } from 'lucide-react';
import { loanFiles, stageColors, type LoanFile } from './data';
type SortKey = 'name' | 'amount' | 'days' | 'stage';
const STAGES: LoanFile['stage'][] = ['Processing', 'Underwriting', 'CD Sent', 'Clear to Close', 'Closed', 'At Risk'];
const LOAN_TYPES: LoanFile['loanType'][] = ['Conventional', 'FHA', 'VA', 'Jumbo'];
export function ActivePipeline() {
  const [search, setSearch] = useState('');
  const [stageFilter, setStageFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [riskOnly, setRiskOnly] = useState(false);
  const [sortKey, setSortKey] = useState<SortKey>('days');
  const [sortDesc, setSortDesc] = useState(true);
  const filtered = useMemo(() => {
    let files = [...loanFiles];
    if (search.trim()) {
      const q = search.toLowerCase();
      files = files.filter(f => f.borrowerName.toLowerCase().includes(q) || f.loanNumber.toLowerCase().includes(q));
    }
    if (stageFilter !== 'all') files = files.filter(f => f.stage === stageFilter);
    if (typeFilter !== 'all') files = files.filter(f => f.loanType === typeFilter);
    if (riskOnly) files = files.filter(f => f.isAtRisk || f.notTouched);
    files.sort((a, b) => {
      let cmp = 0;
      switch (sortKey) {
        case 'name':
          cmp = a.borrowerName.localeCompare(b.borrowerName);
          break;
        case 'amount':
          cmp = a.loanAmount - b.loanAmount;
          break;
        case 'days':
          cmp = a.daysInStage - b.daysInStage;
          break;
        case 'stage':
          cmp = a.stage.localeCompare(b.stage);
          break;
      }
      return sortDesc ? -cmp : cmp;
    });
    return files;
  }, [search, stageFilter, typeFilter, riskOnly, sortKey, sortDesc]);
  const stageCounts = useMemo(() => {
    const counts: Record<string, number> = {
      all: loanFiles.length
    };
    STAGES.forEach(s => {
      counts[s] = loanFiles.filter(f => f.stage === s).length;
    });
    return counts;
  }, []);
  const totalVolume = filtered.reduce((sum, f) => sum + f.loanAmount, 0);
  const totalPoints = filtered.reduce((sum, f) => sum + Object.values(f.pointsByMetric).reduce((s, v) => s + v, 0), 0);
  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortDesc(!sortDesc);else {
      setSortKey(key);
      setSortDesc(true);
    }
  };
  const fmtMoney = (n: number) => new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0
  }).format(n);
  const hasActiveFilters = search.trim() || stageFilter !== 'all' || typeFilter !== 'all' || riskOnly;
  const clearAll = () => {
    setSearch('');
    setStageFilter('all');
    setTypeFilter('all');
    setRiskOnly(false);
  };
  return <motion.section initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.4,
    delay: 0.4
  }} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-id="element-20">
      <div className="bg-[#0A1F5C] text-white px-4 py-2.5 flex items-center justify-between gap-2 flex-wrap" data-id="element-21">
        <div className="flex items-center gap-2 font-semibold text-sm tracking-wide" data-id="element-22">
          <LayersIcon className="w-4 h-4" data-id="element-23" />
          ACTIVE PIPELINE
          <span className="ml-1 text-[10px] font-bold bg-white/15 px-2 py-0.5 rounded-full uppercase tracking-wider" data-id="element-24">
            {loanFiles.length} Files
          </span>
        </div>
        <div className="hidden md:flex items-center gap-3 text-[10px] font-bold text-blue-200 uppercase tracking-wider" data-id="element-25">
          <span data-id="element-26">
            Volume:{' '}
            <span className="text-white tabular-nums" data-id="element-27">
              {fmtMoney(totalVolume)}
            </span>
          </span>
          <span className="text-blue-300" data-id="element-28">·</span>
          <span data-id="element-29">
            Points:{' '}
            <span className="text-white tabular-nums" data-id="element-30">
              {totalPoints.toLocaleString()}
            </span>
          </span>
        </div>
      </div>

      {/* Filter bar */}
      <div className="border-b border-gray-200 bg-gray-50 p-4" data-id="element-31">
        <div className="flex flex-col lg:flex-row gap-3" data-id="element-32">
          <div className="relative flex-1 min-w-[200px]" data-id="element-33">
            <SearchIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" data-id="element-34" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by borrower or loan #" className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#0A1F5C]/20 focus:border-[#0A1F5C]" data-id="element-35" />
          </div>

          <select value={stageFilter} onChange={e => setStageFilter(e.target.value)} className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white font-medium text-[#0A1F5C] focus:outline-none focus:ring-2 focus:ring-[#0A1F5C]/20 focus:border-[#0A1F5C]" data-id="element-36">
            <option value="all" data-id="element-37">All stages ({stageCounts.all})</option>
            {STAGES.map(s => <option key={s} value={s} data-id="element-38">
                {s} ({stageCounts[s]})
              </option>)}
          </select>

          <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white font-medium text-[#0A1F5C] focus:outline-none focus:ring-2 focus:ring-[#0A1F5C]/20 focus:border-[#0A1F5C]" data-id="element-39">
            <option value="all" data-id="element-40">All loan types</option>
            {LOAN_TYPES.map(t => <option key={t} value={t} data-id="element-41">
                {t}
              </option>)}
          </select>

          <button type="button" onClick={() => setRiskOnly(!riskOnly)} className={`px-3 py-2 text-[11px] font-bold uppercase tracking-wider border rounded-lg flex items-center gap-2 transition-colors ${riskOnly ? 'bg-red-600 border-red-600 text-white' : 'bg-white border-gray-200 text-red-600 hover:bg-red-50'}`} data-id="element-42">
            <AlertTriangleIcon className="w-3.5 h-3.5" data-id="element-43" />
            At Risk Only
          </button>

          {hasActiveFilters && <button type="button" onClick={clearAll} className="px-3 py-2 text-[11px] font-bold uppercase tracking-wider text-gray-500 hover:text-[#0A1F5C] flex items-center gap-1 transition-colors" data-id="element-44">
              <XIcon className="w-3 h-3" data-id="element-45" />
              Clear
            </button>}
        </div>

        {/* Quick stage chips */}
        <div className="flex flex-wrap gap-1.5 mt-3" data-id="element-46">
          <button type="button" onClick={() => setStageFilter('all')} className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full border transition-colors ${stageFilter === 'all' ? 'bg-[#0A1F5C] text-white border-[#0A1F5C]' : 'bg-white text-gray-600 border-gray-200 hover:border-[#0A1F5C] hover:text-[#0A1F5C]'}`} data-id="element-47">
            All · {stageCounts.all}
          </button>
          {STAGES.map(s => <button type="button" key={s} onClick={() => setStageFilter(s)} className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full border transition-colors ${stageFilter === s ? 'bg-[#0A1F5C] text-white border-[#0A1F5C]' : 'bg-white text-gray-600 border-gray-200 hover:border-[#0A1F5C] hover:text-[#0A1F5C]'}`} data-id="element-48">
              {s} · {stageCounts[s]}
            </button>)}
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto" data-id="element-49">
        {filtered.length === 0 ? <div className="flex flex-col items-center justify-center py-16 text-gray-400" data-id="element-50">
            <FilterIcon className="w-10 h-10 mb-3" data-id="element-51" />
            <p className="font-semibold text-sm" data-id="element-52">
              No files match these filters
            </p>
            <button type="button" onClick={clearAll} className="mt-3 text-xs font-bold text-[#2563EB] uppercase tracking-wider" data-id="element-53">
              Clear filters
            </button>
          </div> : <table className="w-full text-sm" data-id="element-54">
            <thead className="bg-white border-b border-gray-200" data-id="element-55">
              <tr data-id="element-56">
                <SortHeader label="Borrower / Loan #" active={sortKey === 'name'} desc={sortDesc} onClick={() => toggleSort('name')} data-id="element-57" />
                <SortHeader label="Stage" active={sortKey === 'stage'} desc={sortDesc} onClick={() => toggleSort('stage')} data-id="element-58" />
                <SortHeader label="Amount" active={sortKey === 'amount'} desc={sortDesc} onClick={() => toggleSort('amount')} align="right" data-id="element-59" />
                <SortHeader label="Days In Stage" active={sortKey === 'days'} desc={sortDesc} onClick={() => toggleSort('days')} align="right" data-id="element-60" />
                <th className="px-4 py-2.5 text-right text-[10px] font-bold text-gray-500 uppercase tracking-wider" data-id="element-61">
                  MTD Points
                </th>
                <th className="px-4 py-2.5 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider" data-id="element-62">
                  Contributing To
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100" data-id="element-63">
              {filtered.map(f => {
            const filePoints = Object.values(f.pointsByMetric).reduce((s, v) => s + v, 0);
            return <tr key={f.id} className="hover:bg-gray-50 transition-colors" data-id="element-64">
                    <td className="px-4 py-3" data-id="element-65">
                      <div className="font-semibold text-[#0A1F5C] text-sm" data-id="element-66">
                        {f.borrowerName}
                      </div>
                      <div className="text-[10px] text-gray-500 font-mono" data-id="element-67">
                        {f.loanNumber} · {f.loanType}
                      </div>
                    </td>
                    <td className="px-4 py-3" data-id="element-68">
                      <span className={`inline-block text-[10px] font-bold uppercase px-2 py-0.5 rounded border tracking-wider ${stageColors[f.stage]}`} data-id="element-69">
                        {f.stage}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums font-semibold text-[#0A1F5C]" data-id="element-70">
                      {fmtMoney(f.loanAmount)}
                    </td>
                    <td className="px-4 py-3 text-right" data-id="element-71">
                      <div className="flex items-center justify-end gap-1.5" data-id="element-72">
                        <ClockIcon className={`w-3.5 h-3.5 ${f.daysInStage > 5 ? 'text-red-500' : 'text-gray-400'}`} data-id="element-73" />
                        <span className={`tabular-nums font-bold text-sm ${f.daysInStage > 5 ? 'text-red-600' : 'text-[#0A1F5C]'}`} data-id="element-74">
                          {f.daysInStage}d
                        </span>
                      </div>
                      <div className="text-[10px] text-gray-500" data-id="element-75">
                        {f.lastTouched}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right" data-id="element-76">
                      <span className={`text-base font-bold tabular-nums ${filePoints > 0 ? 'text-[#16A34A]' : filePoints < 0 ? 'text-red-600' : 'text-gray-400'}`} data-id="element-77">
                        {filePoints > 0 ? '+' : ''}
                        {filePoints.toLocaleString()}
                      </span>
                    </td>
                    <td className="px-4 py-3" data-id="element-78">
                      <div className="flex flex-wrap gap-1" data-id="element-79">
                        {f.metrics.length === 0 ? <span className="text-[10px] text-gray-400 italic" data-id="element-80">
                            No contribution yet
                          </span> : f.metrics.map(m => <MetricChip key={m} id={m} data-id="element-81" />)}
                      </div>
                    </td>
                  </tr>;
          })}
            </tbody>
          </table>}
      </div>

      <div className="border-t border-gray-200 bg-gray-50 px-5 py-3 flex justify-between items-center text-xs flex-wrap gap-2" data-id="element-82">
        <span className="font-bold text-gray-500 uppercase tracking-wider" data-id="element-83">
          Showing {filtered.length} of {loanFiles.length} files
        </span>
        <span className="font-bold text-[#0A1F5C] uppercase tracking-wider" data-id="element-84">
          Volume:{' '}
          <span className="text-[#2563EB] tabular-nums" data-id="element-85">
            {fmtMoney(totalVolume)}
          </span>
          <span className="mx-2 text-gray-300" data-id="element-86">·</span>
          Points:{' '}
          <span className="text-[#16A34A] tabular-nums" data-id="element-87">
            {totalPoints.toLocaleString()}
          </span>
        </span>
      </div>
    </motion.section>;
}
function SortHeader({
  label,
  active,
  desc,
  onClick,
  align = 'left'
}: {
  label: string;
  active: boolean;
  desc: boolean;
  onClick: () => void;
  align?: 'left' | 'right';
}) {
  return <th onClick={onClick} className={`px-4 py-2.5 text-${align} text-[10px] font-bold uppercase tracking-wider cursor-pointer select-none transition-colors ${active ? 'text-[#0A1F5C]' : 'text-gray-500 hover:text-[#0A1F5C]'}`} data-id="element-88">
      <span className={`inline-flex items-center gap-1 ${align === 'right' ? 'flex-row-reverse' : ''}`} data-id="element-89">
        {label}
        <ArrowUpDownIcon className={`w-3 h-3 ${active ? 'opacity-100' : 'opacity-30'}`} data-id="element-90" />
      </span>
    </th>;
}
function MetricChip({
  id
}: {
  id: string;
}) {
  const map: Record<string, {
    label: string;
    cls: string;
  }> = {
    conditions: {
      label: 'Conditions',
      cls: 'bg-green-50 text-green-700 border-green-200'
    },
    cd: {
      label: 'CD Sent',
      cls: 'bg-purple-50 text-purple-700 border-purple-200'
    },
    speed: {
      label: 'Speed',
      cls: 'bg-orange-50 text-orange-700 border-orange-200'
    },
    closed: {
      label: 'Closed',
      cls: 'bg-blue-50 text-blue-700 border-blue-200'
    },
    penalty: {
      label: 'Penalty',
      cls: 'bg-red-50 text-red-700 border-red-200'
    }
  };
  const m = map[id];
  if (!m) return null;
  return <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border ${m.cls}`} data-id="element-91">
      {m.label}
    </span>;
}